import { observer } from 'mobx-react';
import { When } from 'react-if';
import { useTranslation } from 'react-i18next';
import { Popover, theme } from 'antd';

import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { useValueByConnectivity } from '@/hooks/useValueByConnectivity';
import { AlertsWidget } from '@/comp/MapBuilder/components/AlertsWidget';
import { Ports } from '@/comp/MapBuilder/components/Ports';
import RefrigeratedAirDryerWidget from '@/comp/common/icons/RefrigeratedAirDryerWidget';
import RefrigeratedAirDryerMaintenanceWidget from '@/comp/common/icons/RefrigeratedAirDryerMaintenanceWidget';

import { GetConnectivityIconAndStatus } from './GetConnectivityIconAndStatus';
import { getWidgetFillColor, isSelectedNodeOnMap } from '../../utils/utils';
import { Container } from './RefrigeratedAirDryerNodeWidget.styles';
import PackageCareIndication from '../nodeWidget/DetailsDrawer/PackageCareIndication/PackageCareIndication';
import { DUMMY_MODEL_IDS } from '../nodeWidget/EditAssetDrawer/EditAssetDrawer.utils';
import { getBorderInfo } from '../compressor/CompressorNodeWidget.utils';

const { useToken } = theme;

export const RefrigeratedAirDryerNodeWidget = observer(({ node, engine, width, height }) => {
  const { t } = useTranslation();
  const { token } = useToken();
  const { tunit, tvalue } = useMeasurement();
  const { tcunit, tcvalue } = useValueByConnectivity();
  const { name, tempCelsius, connectivity, activity, inMaintenance, deviceId, deviceModelId } = node.getEntity();
  const { mapBuilderStore } = useStores();

  const renderedOnMap = !!engine;
  const isSelected = isSelectedNodeOnMap(deviceId, mapBuilderStore.selectedDevice?.deviceId, renderedOnMap);
  const backgroundColor = getWidgetFillColor(isSelected, token, renderedOnMap);
  const hasNoMatchingSpec = deviceModelId === DUMMY_MODEL_IDS.refrigeratedAirDryer && renderedOnMap;
  const borderInfo = getBorderInfo(isSelected, inMaintenance, false, hasNoMatchingSpec, token);

  const connectivityIconAndStatus = GetConnectivityIconAndStatus({
    connectivity: connectivity,
    activity: activity,
  });

  return (
    <Container
      width={width}
      height={height}
      $mapView={engine ? -1 : 1}
      $isSelectedOnMap={isSelected}
      $textColor={borderInfo?.textColor}
    >
      {borderInfo ? (
        <RefrigeratedAirDryerMaintenanceWidget
          width={width}
          height={height}
          fillColor={borderInfo.fillColor}
          borderColor={borderInfo.borderColor}
          className="background"
        />
      ) : (
        <RefrigeratedAirDryerWidget width={width} height={height} color={backgroundColor} className="background" />
      )}

      <div className="top-row">
        {renderedOnMap && (
          <header>
            <Popover content={name}>
              <div className="title-wrapper">
                <div className="ellipsis name">{name}</div>
                <div className="ellipsis type">{t('Refrigerated')}</div>
              </div>
            </Popover>
          </header>
        )}
        <div className="connectivity-wrapper">{connectivityIconAndStatus.icon}</div>
      </div>

      <When condition={renderedOnMap}>
        {borderInfo && <div className="title">{t(borderInfo.headerText)}</div>}

        <section>
          {tempCelsius != null && (
            <div className="temperature-container">
              <span className="value">{tcvalue(tvalue(tempCelsius, 'celsius'), connectivity)}</span>
              <span className="label">{tcunit(tunit('°C'), tempCelsius, connectivity)}</span>
            </div>
          )}
          <AlertsWidget alerts={node.alerts} />
          <PackageCareIndication
            entity={node.getEntity()}
            mapView
            className="package-care-wrapper"
            backgroundColor={borderInfo?.fillColor}
          />
        </section>

        <Ports node={node} engine={engine} width={width} height={height} />
      </When>
    </Container>
  );
});
